import CheckIcon from "@mui/icons-material/Check";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { Stack } from "@mui/system";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../../assets/styles/tokens";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUserToken } from "../../../../../../../../../../features/userSlice";
import { useSearchParams } from "react-router-dom";
import {
  last30DaysQueryIdWebsite,
  last6WeeksQueryIdWebsite,
  last12MonthsFullQueryIdWebsite,
  last12MonthsProportionalQueryIdWebsite,
} from "../../../../../../../../../AssetManagement/Overview/BusinessLeaderDashboard/BizLeaderMobileDashboard/helpers/QIDs";
import { BizDevelopmentDashboardMobileEndPointLogic } from "../../../../../../../../../../logic/BizDevelopment/Dashboard/BizDevelopmentDashboardMobileEndPointLogic";
import { setLoadingOverviewAccountSummaryData } from "../../../../../../../../../../features/bizDevelopmentDashboardMobileSlice";

const AccountSummeryDateFilter = ({ refreshKey }: { refreshKey?: number }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const periodFromUrl = searchParams.get("periodFromWebsite");
  const { loadingOverviewAccountSummaryData } = useSelector(
    (state: any) => state.bizDevelopmentDashboardMobile,
  );
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const dispatch = useDispatch();

  const websiteSharedUids = searchParams.get("workspace");
  const queryConfigMap: Record<
    string,
    { queryId: string; period_type?: "Full" | "Proportional" }
  > = {
    "Last 30 Days": {
      queryId: last30DaysQueryIdWebsite,
    },
    "Last 6 Weeks": {
      queryId: last6WeeksQueryIdWebsite,
    },
    "Last 12 Months Full": {
      queryId: last12MonthsFullQueryIdWebsite,
      period_type: "Full",
    },
    "Last 12 Month Proportional": {
      queryId: last12MonthsProportionalQueryIdWebsite,
      period_type: "Proportional",
    },
  };

  const handleChange = (_: any, newValue: string | null) => {
    if (!newValue) return;
    setValue(newValue);
    let paramValue;
    if (newValue === "Last 30 Days") {
      paramValue = "last30";
    } else {
      paramValue = periodParamMap[newValue];
    }
    const newParams = new URLSearchParams(searchParams);
    newParams.set("periodFromWebsite", paramValue);
    setSearchParams(newParams);
  };

  const getLast30DaysLabel = () => {
    const today = new Date();
    const endDate = new Date(today);
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 29);
    const format = (date: Date) => date.toLocaleDateString("en-GB"); // dd/mm/yyyy
    return `Last 30 Days, ${format(startDate)} - ${format(endDate)}`;
  };

  const getLast6WeeksLabel = () => {
    const today = new Date();
    const endDate = new Date(today);
    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 41);
    const format = (date: Date) => date.toLocaleDateString("en-GB");
    return `Last 6 Weeks, ${format(startDate)} - ${format(endDate)}`;
  };
  const options = [
    "Last 30 Days",
    "Last 6 Weeks",
    "Last 12 Months Full",
    "Last 12 Month Proportional",
  ];
  const periodParamMap: Record<string, string> = {
    "Last 6 Weeks": "last6weeks",
    "Last 12 Months Full": "last12full",
    "Last 12 Month Proportional": "last12prop",
  };

  const reversePeriodMap: Record<string, string> = {
    last30: "Last 30 Days",
    last6weeks: "Last 6 Weeks",
    last12full: "Last 12 Months Full",
    last12prop: "Last 12 Month Proportional",
  };
  const [value, setValue] = useState<string | null>(() => {
    if (!periodFromUrl) return "Last 30 Days";
    return reversePeriodMap[periodFromUrl] || "Last 30 Days";
  });
  const getDisplayLabel = (option: string) => {
    if (option === "Last 30 Days") {
      return getLast30DaysLabel();
    }
    if (option === "Last 6 Weeks") {
      return getLast6WeeksLabel();
    }
    return option;
  };
  useEffect(() => {
    if (!value) return;
    const configKey = value;
    const { queryId, period_type } = queryConfigMap[configKey];
    const fetchData = async () => {
      try {
        dispatch(setLoadingOverviewAccountSummaryData(true));
        const dimensionResponse =
          await BizDevelopmentDashboardMobileEndPointLogic.getDimensionInBizDashboardMobile(
            oauthTokenHeader,
          );
        if (!dimensionResponse || dimensionResponse.status !== 200) return;
        const dimensions = dimensionResponse?.data?.data || [];
        const matchedDimensions = dimensions
          ?.filter((dim: any) =>
            dim.websites?.some(
              (site: any) => site.shared_uid === websiteSharedUids,
            ),
          )
          ?.map((dim: any) => dim.dimension);
        const dimensionsToUse =
          matchedDimensions?.length > 0
            ? matchedDimensions
            : dimensions?.length > 0
              ? [dimensions[0].dimension]
              : [];

        if (!dimensionsToUse.length) return;
        await BizDevelopmentDashboardMobileEndPointLogic.getOverviewAccountSummaryDataInBizLeaderDashboardMobile(
          {
            oauthTokenHeader,
            queryId: queryId,
            websiteSharedUids: websiteSharedUids?.split(",") || [],
            type: "site",
            dimension: dimensionsToUse,
            period_type,
          },
        );
      } finally {
        dispatch(setLoadingOverviewAccountSummaryData(false));
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, websiteSharedUids, refreshKey]);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          mt: spacing.Spacing.xs.$value,
        }}
      >
        <Autocomplete
          options={options}
          value={value}
          getOptionLabel={(option) => getDisplayLabel(option)}
          onChange={handleChange}
          disabled={loadingOverviewAccountSummaryData}
          sx={{
            width: "100%",
            "& .MuiInputBase-input": {
              fontSize: typography.Size.Text.sm.$value,
              lineHeight: typography.Line_height.text.sm.$value,
              fontWeight: typography.Font_weight.Semibold.$value,
              py: "0px !important",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: spacing.Radius.md.$value,
              "&.Mui-focused fieldset": {
                borderColor: colors.GrayLightMode[300].$value,
                borderWidth: "1px",
              },
            },
          }}
          size="small"
          renderOption={(props, option, { selected }) => (
            <li
              {...props}
              style={{
                display: "flex",
                alignItems: "center",
                paddingLeft: spacing.Spacing["5xl"].$value,
                position: "relative",
                backgroundColor: selected ? colors.Brand[50].$value : "",
              }}
            >
              {selected && (
                <CheckIcon
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    left: spacing.Spacing.lg.$value,
                  }}
                />
              )}
              <span style={{}}>{option}</span>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              color="secondary"
              {...params}
              value={value ? getDisplayLabel(value) : ""}
              inputProps={{ ...params.inputProps, readOnly: true }}
            />
          )}
        />
      </Stack>
    </>
  );
};

export default AccountSummeryDateFilter;
